import { IPinpadService } from "modules/payment/application/interfaces/IPinpadService";
import { Payment } from "modules/payment/domain/models/IPayment";
import { TransactionType } from "modules/payment/domain/models/TransactionType";
import { Kiosk } from "domains/kiosk/aggregates/Kiosk";
import enviroment from "corss-cutting/enviroment";
import { getAdyenDeviceIdOverride } from "Infrastructure/repositories/localStorage/adyenFlag";
import { HttpRepository } from "Infrastructure/repositories/api/_HttpRepository";
import { PaymentAcquirerType } from "domains/order/services/acquirerType";
import {
  SimplePaymentRequest,
  SimplePaymentResponse,
  SimpleReversalRequest,
  SimpleReversalResponse,
} from "./SimplePaymentTypes";

export const resolveAdyenDeviceId = (kiosk: Kiosk | undefined): string =>
  getAdyenDeviceIdOverride() ?? kiosk?.adyenDeviceId ?? enviroment.adyenDeviceId ?? "";

export const resolveAdyenSaleId = (kiosk: Kiosk | undefined): string =>
  kiosk?.id?.trim() || "meep-totem";

// Enum BandeiraCartao espelhado do backend/POS Clássica (promptus.app.meep.domain.enums.BandeiraCartao):
// visa=1, mastercard=2, elo=3, amex=4, hipercard=5, discover=6, diners=7, jcb=8, aura=9.
// A Adyen retorna cardBrand em códigos próprios (brand/paymentMethod da Terminal API), daí o mapa abaixo.
const ADYEN_CARD_BRAND_TO_BANDEIRA_CARTAO: Record<string, number> = {
  visa: 1,
  visadankort: 1,
  mc: 2,
  mccredit: 2,
  mcdebit: 2,
  maestro: 2,
  elo: 3,
  elocredit: 3,
  elodebit: 3,
  amex: 4,
  hipercard: 5,
  discover: 6,
  diners: 7,
  jcb: 8,
  aura: 9,
};

const resolveBandeiraCartao = (cardBrand: string | null): number =>
  ADYEN_CARD_BRAND_TO_BANDEIRA_CARTAO[cardBrand?.toLowerCase() ?? ""] ?? 0;

const toPayment = (response: SimplePaymentResponse, transactionType: TransactionType, amount: number): Payment => {
  const customerReceipt = response.receipts?.find((r) => r.documentQualifier === "CustomerReceipt");

  return {
    success: response.success,
    errorMessage: response.errorMessage,
    cardHash: null,
    amount,
    cardHolder: "",
    cardNumber: response.cardNumber ?? "",
    codigoTransacaoTEF: response.poiTransactionId,
    comprovanteTEF: customerReceipt?.receiptText ?? "",
    paymentMethod: transactionType,
    FinancialTransactionModel: {
      CodigoEmpresa: "",
      BandeiraCartao: resolveBandeiraCartao(response.cardBrand),
      CodigoAdquirente: "",
      DataCriacao: response.poiTransactionTimestamp ?? new Date().toISOString(),
      FinalCartao: response.cardNumber ?? "",
      Parcelas: 1,
      Status: response.success ? 1 : 0,
      TipoAdquirente: PaymentAcquirerType.Adyen,
      TipoPagamento: transactionType,
      Valor: amount.toString(),
      NumeroCupom: "",
      NSU: response.poiTransactionId ?? undefined,
      CodigoAutorizacao: response.authorizationCode ?? undefined,
    },
  };
};

export const AdyenPaymentRepository = (): IPinpadService => {
  // Guarda só o necessário para montar o estorno (o receiptText já veio pronto em payAsync,
  // não precisa do payload inteiro). Em memória — cancelPaymentAsync não tem hoje nenhum
  // caller de UI real no totem, então não há expectativa de estornar após reload de página.
  let lastTransaction: { transactionId: string; transactionTimestamp: string } | null = null;
  let lastDeviceId = "";
  let lastSaleId = "";

  const payAsync = async (
    amount: number,
    transactionType: TransactionType,
    kiosk?: Kiosk,
    orderId?: string
  ): Promise<Payment> => {
    const httpRepository = HttpRepository();
    const deviceId = resolveAdyenSaleId(kiosk);
    const adyenDeviceId = resolveAdyenDeviceId(kiosk);

    const request: SimplePaymentRequest = {
      amount,
      transactionType,
      deviceId,
      adyenDeviceId,
      orderId,
      localId: kiosk?.localId,
      deviceName: kiosk?.kioskName,
    };

    const response = await httpRepository.postAsync<SimplePaymentRequest, SimplePaymentResponse>(
      `${enviroment.adyenBackendUrl}/simplepayment`,
      request
    );

    if (response.poiTransactionId && response.poiTransactionTimestamp) {
      lastTransaction = {
        transactionId: response.poiTransactionId,
        transactionTimestamp: response.poiTransactionTimestamp,
      };
      lastDeviceId = deviceId;
      lastSaleId = adyenDeviceId;
    }

    return toPayment(response, transactionType, amount);
  };

  const cancelPaymentAsync = async (
    value: number,
    typePayment: TransactionType,
    couponNumber: string,
    paymentDate: string
  ): Promise<boolean> => {
    if (!lastTransaction) {
      throw new Error("Nenhuma transação Adyen registrada para estornar.");
    }

    const httpRepository = HttpRepository();
    const request: SimpleReversalRequest = {
      originalTransactionId: lastTransaction.transactionId,
      originalTransactionTimestamp: lastTransaction.transactionTimestamp,
      deviceId: lastDeviceId,
      adyenDeviceId: lastSaleId,
    };

    const response = await httpRepository.postAsync<SimpleReversalRequest, SimpleReversalResponse>(
      `${enviroment.adyenBackendUrl}/simplepayment/reversal`,
      request
    );

    return response.success;
  };

  return { payAsync, cancelPaymentAsync };
};
